import type {
  ElectiveDefinition,
  ElectiveGroup,
  EmbeddedAward,
  Module,
  Programme,
  ProgrammeVersion,
  Stage,
} from "@/types";

import { resolveEffectiveAssessments } from "./assessment-overrides";
import { getElectiveOptionSelection } from "./electives";

export type AwardCreditStatus = "aligned" | "shortfall" | "excess" | "incomplete";

export interface AwardCreditProjection {
  award: EmbeddedAward;
  linkedModules: Module[];
  minimumCredits: number;
  maximumCredits: number;
  declaredCredits: number;
  status: AwardCreditStatus;
  incompleteChoiceSlots: string[];
  applicableVersionLabels: string[];
}

export interface ProgrammePathwayOptionProjection {
  option: ElectiveGroup;
  selectedModules: Module[];
  credits: number;
  complete: boolean;
}

export interface ProgrammePathwayChoiceSlotProjection {
  choiceSlot: ElectiveDefinition;
  representativeOption?: ProgrammePathwayOptionProjection;
  optionCount: number;
  complete: boolean;
}

export interface ProgrammePathwayProjection {
  mandatoryModules: Module[];
  choiceSlots: ProgrammePathwayChoiceSlotProjection[];
  selectedModules: Module[];
  unassignedElectiveModules: Module[];
  declaredCredits: number;
  projectedCredits: number;
  status: AwardCreditStatus;
  incompleteChoiceSlots: string[];
}

export type ProgrammeVersionCoverageStatus =
  "covered" | "missing-modules" | "no-structure" | "pathway-incomplete" | "credit-target-mismatch";

export interface ProgrammeVersionPathwayCoverage {
  version: ProgrammeVersion;
  stageCount: number;
  coveredModules: Module[];
  missingModules: Module[];
  coveredCredits: number;
  stageCreditsTarget: number;
  applicableAwards: EmbeddedAward[];
  status: ProgrammeVersionCoverageStatus;
}

export interface EmbeddedAwardEvidenceScope {
  versionId?: string;
  versionLabel: string;
  assessmentCount: number;
  moduleOutcomeCount: number;
  assessedModuleOutcomeCount: number;
  programmeOutcomeCount: number;
  supportedProgrammeOutcomeCount: number;
  modulesWithoutOutcomes: Module[];
  unassessedOutcomeLabels: string[];
}

export interface EmbeddedAwardEvidenceOverview {
  award: EmbeddedAward;
  linkedModules: Module[];
  scopes: EmbeddedAwardEvidenceScope[];
}

export interface AwardPathwayElectiveLaneChoice {
  definition: ElectiveDefinition;
  modules: Module[];
  requiredCredits: number;
  offeredCredits: number;
}

export interface AwardPathwayStageLane {
  stage: Stage;
  mandatoryModules: Module[];
  mandatoryCredits: number;
  electiveChoices: AwardPathwayElectiveLaneChoice[];
  ungroupedElectiveModules: Module[];
  electiveCreditsRequired: number;
  electiveCreditsOffered: number;
  routeCredits: number;
  milestones: EmbeddedAward[];
}

export interface AwardPathwayDiagram {
  version: ProgrammeVersion;
  stages: AwardPathwayStageLane[];
  routeCredits: number;
}

function stageModuleIds(stage: Stage): string[] {
  return [
    ...(stage.modules ?? []).map((assignment) => assignment.moduleId),
    ...(stage.moduleIds ?? []),
  ];
}

function uniqueModules(modules: Module[]): Module[] {
  return Array.from(new Map(modules.map((module) => [module.id, module])).values());
}

function stageElectiveDefinitions(
  programme: Programme,
  stage: Stage,
  placedModuleIds: Set<string>,
): ElectiveDefinition[] {
  const definitions = programme.electiveDefinitions ?? [];
  const explicitIds = new Set(
    (stage.electiveChoiceSlots ?? []).map((slot) => slot.electiveDefinitionId),
  );
  const explicit = definitions.filter((definition) => explicitIds.has(definition.id));
  if (explicit.length > 0) {
    return explicit;
  }
  return definitions.filter((definition) =>
    (definition.groups ?? []).some((group) =>
      (group.moduleIds ?? []).some((moduleId) => placedModuleIds.has(moduleId)),
    ),
  );
}

function milestoneStageIndex(award: EmbeddedAward, stages: Stage[]): number | undefined {
  const explicitStageIds = new Set(award.stageIds ?? []);
  if (explicitStageIds.size > 0) {
    const linkedIndexes = stages
      .map((stage, index) => (explicitStageIds.has(stage.id) ? index : -1))
      .filter((index) => index >= 0);
    return linkedIndexes.length > 0 ? Math.max(...linkedIndexes) : undefined;
  }
  const requiredModuleIds = award.moduleIds ?? [];
  if (requiredModuleIds.length === 0) {
    return undefined;
  }
  const cumulativeModuleIds = new Set<string>();
  for (let index = 0; index < stages.length; index += 1) {
    stageModuleIds(stages[index]).forEach((moduleId) => cumulativeModuleIds.add(moduleId));
    if (requiredModuleIds.every((moduleId) => cumulativeModuleIds.has(moduleId))) {
      return index;
    }
  }
  return undefined;
}

function awardAppliesToVersion(award: EmbeddedAward, version: ProgrammeVersion): boolean {
  const versionIds = award.versionIds ?? [];
  const stageIds = new Set(award.stageIds ?? []);
  if (versionIds.length === 0 && stageIds.size === 0) {
    return true;
  }
  return (
    versionIds.includes(version.id) ||
    (version.stages ?? []).some((stage) => stageIds.has(stage.id))
  );
}

const EXIT_KINDS = new Set<EmbeddedAward["kind"]>(["exit", "embedded-and-exit"]);

/**
 * The awards a learner can leave with on completing each stage of a version,
 * keyed by stage id: exit awards whose pathway ends at that stage.
 *
 * @param programme - The programme whose awards to place.
 * @param version - The delivery version whose stages they end at.
 * @returns Each stage's exit awards; a stage with none is absent.
 */
export function exitAwardsByStage(
  programme: Pick<Programme, "embeddedAwards">,
  version: ProgrammeVersion,
): Map<string, EmbeddedAward[]> {
  const stages = [...(version.stages ?? [])].sort(
    (left, right) => Number(left.sequence ?? 0) - Number(right.sequence ?? 0),
  );
  const byStage = new Map<string, EmbeddedAward[]>();
  (programme.embeddedAwards ?? [])
    .filter((award) => EXIT_KINDS.has(award.kind) && awardAppliesToVersion(award, version))
    .forEach((award) => {
      const index = milestoneStageIndex(award, stages);
      const stage = index === undefined ? undefined : stages[index];
      if (stage) {
        byStage.set(stage.id, [...(byStage.get(stage.id) ?? []), award]);
      }
    });
  return byStage;
}

/** The exit award titles a stage offers, joined, or blank when it offers none. */
export function exitAwardTitle(awards: EmbeddedAward[] | undefined): string {
  return (awards ?? []).map((award) => award.title.trim() || "Exit Award Available").join("; ");
}

/**
 * Builds the stage-by-stage mandatory and elective lanes for one programme version.
 */
export function getAwardPathwayDiagram(
  programme: Programme,
  versionId: string,
): AwardPathwayDiagram | undefined {
  const version = (programme.versions ?? []).find((candidate) => candidate.id === versionId);
  if (!version) {
    return undefined;
  }
  const modulesById = new Map((programme.modules ?? []).map((module) => [module.id, module]));
  const allElectiveModuleIds = new Set(
    (programme.electiveDefinitions ?? []).flatMap((definition) =>
      (definition.groups ?? []).flatMap((group) => group.moduleIds ?? []),
    ),
  );
  (programme.modules ?? []).forEach((module) => {
    if (module.isElective) {
      allElectiveModuleIds.add(module.id);
    }
  });
  const stages = [...(version.stages ?? [])].sort(
    (left, right) => Number(left.sequence ?? 0) - Number(right.sequence ?? 0),
  );
  const milestoneIndexes = new Map(
    (programme.embeddedAwards ?? [])
      .filter((award) => awardAppliesToVersion(award, version))
      .map((award) => [award.id, milestoneStageIndex(award, stages)]),
  );

  const stageLanes = stages.map((stage, stageIndex) => {
    const placedModuleIds = new Set(stageModuleIds(stage));
    const placedModules = uniqueModules(
      Array.from(placedModuleIds)
        .map((moduleId) => modulesById.get(moduleId))
        .filter((module): module is Module => Boolean(module)),
    );
    const mandatoryModules = placedModules.filter((module) => !allElectiveModuleIds.has(module.id));
    const electiveDefinitions = stageElectiveDefinitions(programme, stage, placedModuleIds);
    const electiveChoices = electiveDefinitions.map((definition) => {
      const modules = uniqueModules(
        (definition.groups ?? []).flatMap((group) =>
          (group.moduleIds ?? [])
            .filter((moduleId) => placedModuleIds.has(moduleId))
            .map((moduleId) => modulesById.get(moduleId))
            .filter((module): module is Module => Boolean(module)),
        ),
      );
      return {
        definition,
        modules,
        requiredCredits: Number(definition.credits ?? 0),
        offeredCredits: modules.reduce((sum, module) => sum + Number(module.credits ?? 0), 0),
      };
    });
    const groupedElectiveModuleIds = new Set(
      electiveChoices.flatMap((choice) => choice.modules.map((module) => module.id)),
    );
    const ungroupedElectiveModules = placedModules.filter(
      (module) => allElectiveModuleIds.has(module.id) && !groupedElectiveModuleIds.has(module.id),
    );
    const mandatoryCredits = mandatoryModules.reduce(
      (sum, module) => sum + Number(module.credits ?? 0),
      0,
    );
    const ungroupedElectiveCredits = ungroupedElectiveModules.reduce(
      (sum, module) => sum + Number(module.credits ?? 0),
      0,
    );
    const electiveCreditsRequired =
      electiveChoices.reduce((sum, choice) => sum + choice.requiredCredits, 0) +
      ungroupedElectiveCredits;
    const electiveCreditsOffered =
      electiveChoices.reduce((sum, choice) => sum + choice.offeredCredits, 0) +
      ungroupedElectiveCredits;

    return {
      stage,
      mandatoryModules,
      mandatoryCredits,
      electiveChoices,
      ungroupedElectiveModules,
      electiveCreditsRequired,
      electiveCreditsOffered,
      routeCredits: mandatoryCredits + electiveCreditsRequired,
      milestones: (programme.embeddedAwards ?? []).filter(
        (award) => milestoneIndexes.get(award.id) === stageIndex,
      ),
    };
  });

  return {
    version,
    stages: stageLanes,
    routeCredits: stageLanes.reduce((sum, stage) => sum + stage.routeCredits, 0),
  };
}

function linkedModuleIds(award: EmbeddedAward, versions: ProgrammeVersion[]): Set<string> {
  const ids = new Set(award.moduleIds ?? []);
  const linkedStages = new Set(award.stageIds ?? []);
  versions.forEach((version) => {
    (version.stages ?? []).forEach((stage) => {
      if (linkedStages.has(stage.id)) {
        stageModuleIds(stage).forEach((moduleId) => ids.add(moduleId));
      }
    });
  });
  return ids;
}

function applicableVersionLabels(award: EmbeddedAward, versions: ProgrammeVersion[]): string[] {
  const explicitVersionIds = new Set(award.versionIds ?? []);
  const linkedStageIds = new Set(award.stageIds ?? []);
  return versions
    .filter(
      (version) =>
        explicitVersionIds.has(version.id) ||
        (version.stages ?? []).some((stage) => linkedStageIds.has(stage.id)),
    )
    .map((version) => version.label || version.code || "Programme version");
}

/**
 * Projects the achievable credit range for one embedded award.
 *
 * Modules linked directly, or through a linked stage, form the award structure.
 * Mandatory modules are counted once. Modules assigned to an elective choice
 * slot are counted through one complete option per slot, producing a range
 * when alternative options carry different credit totals.
 */
export function getAwardCreditProjection(
  programme: Programme,
  award: EmbeddedAward,
): AwardCreditProjection {
  const modules = programme.modules ?? [];
  const versions = programme.versions ?? [];
  const linkedIds = linkedModuleIds(award, versions);
  const linkedModules = modules.filter((module) => linkedIds.has(module.id));
  const electiveModuleIds = new Set(
    (programme.electiveDefinitions ?? []).flatMap((definition) =>
      (definition.groups ?? []).flatMap((group) => group.moduleIds ?? []),
    ),
  );
  const mandatoryCredits = linkedModules
    .filter((module) => !electiveModuleIds.has(module.id))
    .reduce((sum, module) => sum + Number(module.credits ?? 0), 0);

  let minimumCredits = mandatoryCredits;
  let maximumCredits = mandatoryCredits;
  const incompleteChoiceSlots: string[] = [];

  (programme.electiveDefinitions ?? []).forEach((definition) => {
    const optionCredits = (definition.groups ?? [])
      .map((group) => getElectiveOptionSelection(group, linkedModules))
      .filter((selection) => selection.optionModules.length > 0);
    if (optionCredits.length === 0) {
      return;
    }
    const completeOptions = optionCredits.filter(
      (selection) => selection.selectionCountValid && selection.selectionComplete,
    );
    if (completeOptions.length !== optionCredits.length || completeOptions.length === 0) {
      incompleteChoiceSlots.push(definition.name || definition.code || "Untitled choice slot");
    }
    if (completeOptions.length > 0) {
      const credits = completeOptions.map((selection) => selection.credits);
      minimumCredits += Math.min(...credits);
      maximumCredits += Math.max(...credits);
    }
  });

  const declaredCredits = Number(award.credits ?? 0);
  let status: AwardCreditStatus = "aligned";
  if (linkedModules.length === 0 || incompleteChoiceSlots.length > 0) {
    status = "incomplete";
  } else if (declaredCredits > maximumCredits) {
    status = "shortfall";
  } else if (declaredCredits < minimumCredits) {
    status = "excess";
  }

  return {
    award,
    linkedModules,
    minimumCredits,
    maximumCredits,
    declaredCredits,
    status,
    incompleteChoiceSlots,
    applicableVersionLabels: applicableVersionLabels(award, versions),
  };
}

export function getAwardCreditRangeLabel(projection: AwardCreditProjection): string {
  return projection.minimumCredits === projection.maximumCredits
    ? `${projection.minimumCredits} credits`
    : `${projection.minimumCredits}-${projection.maximumCredits} credits`;
}

function outcomeReference(moduleId: string, mimloId: string): string {
  return `${moduleId}:${mimloId}`;
}

function awardVersions(
  award: EmbeddedAward,
  versions: ProgrammeVersion[],
): (ProgrammeVersion | undefined)[] {
  if (versions.length === 0) {
    return [undefined];
  }
  const explicitVersionIds = new Set(award.versionIds ?? []);
  const linkedStageIds = new Set(award.stageIds ?? []);
  const applicable = versions.filter(
    (version) =>
      explicitVersionIds.has(version.id) ||
      (version.stages ?? []).some((stage) => linkedStageIds.has(stage.id)),
  );
  return applicable.length > 0 ? applicable : versions;
}

/**
 * Summarises assessment and outcome evidence for each version of an embedded award.
 *
 * Both assessments coordinated by linked modules and contributions made by linked
 * modules to assessments elsewhere in the programme count as award evidence.
 */
export function getEmbeddedAwardEvidenceOverview(
  programme: Programme,
  award: EmbeddedAward,
): EmbeddedAwardEvidenceOverview {
  const linkedModules = getAwardCreditProjection(programme, award).linkedModules;
  const linkedModuleIdSet = new Set(linkedModules.map((module) => module.id));
  const moduleOutcomeRefs = new Set(
    linkedModules.flatMap((module) =>
      (module.mimlos ?? []).map((mimlo) => outcomeReference(module.id, mimlo.id)),
    ),
  );
  const bareOutcomeIds = new Set(
    linkedModules.flatMap((module) => (module.mimlos ?? []).map((mimlo) => mimlo.id)),
  );
  const modulesWithoutOutcomes = linkedModules.filter(
    (module) => (module.mimlos ?? []).length === 0,
  );

  const scopes = awardVersions(award, programme.versions ?? []).map((version) => {
    const assessedOutcomeRefs = new Set<string>();
    const assessmentIds = new Set<string>();

    (programme.modules ?? []).forEach((coordinatingModule) => {
      const assessments = version
        ? resolveEffectiveAssessments(coordinatingModule, version).assessments
        : (coordinatingModule.assessments ?? []);
      assessments.forEach((assessment) => {
        let contributesEvidence = false;
        if (linkedModuleIdSet.has(coordinatingModule.id)) {
          contributesEvidence = true;
          (assessment.mimloIds ?? []).forEach((mimloId) =>
            assessedOutcomeRefs.add(outcomeReference(coordinatingModule.id, mimloId)),
          );
        }
        (assessment.participatingModules ?? []).forEach((participation) => {
          if (!linkedModuleIdSet.has(participation.moduleId)) {
            return;
          }
          contributesEvidence = true;
          participation.mimloIds.forEach((mimloId) =>
            assessedOutcomeRefs.add(outcomeReference(participation.moduleId, mimloId)),
          );
        });
        if (contributesEvidence) {
          assessmentIds.add(`${coordinatingModule.id}:${assessment.id}`);
        }
      });
    });

    const supportedProgrammeOutcomeCount = (programme.plos ?? []).filter((plo) =>
      (programme.ploToMimlos?.[plo.id] ?? []).some(
        (mapping) => moduleOutcomeRefs.has(mapping) || bareOutcomeIds.has(mapping),
      ),
    ).length;
    const unassessedOutcomeLabels = linkedModules.flatMap((module) =>
      (module.mimlos ?? [])
        .map((mimlo, index) => ({ mimlo, index }))
        .filter(({ mimlo }) => !assessedOutcomeRefs.has(outcomeReference(module.id, mimlo.id)))
        .map(({ index }) => {
          const code = module.code || module.title || "Untitled module";
          return `${code} MIMLO ${index + 1}`;
        }),
    );

    return {
      versionId: version?.id,
      versionLabel: version?.label || version?.code || "Shared structure",
      assessmentCount: assessmentIds.size,
      moduleOutcomeCount: moduleOutcomeRefs.size,
      assessedModuleOutcomeCount: moduleOutcomeRefs.size - unassessedOutcomeLabels.length,
      programmeOutcomeCount: (programme.plos ?? []).length,
      supportedProgrammeOutcomeCount,
      modulesWithoutOutcomes,
      unassessedOutcomeLabels,
    };
  });

  return { award, linkedModules, scopes };
}

function choiceSlotLabel(definition: ElectiveDefinition): string {
  return definition.name || definition.code || "Untitled choice slot";
}

/**
 * Builds one deterministic learner route through the parent programme.
 *
 * The first complete option in each choice slot is used as its representative
 * option. Modules explicitly selected for choose-N options are respected, and
 * duplicate module references are counted once in the programme total.
 */
export function getProgrammePathwayProjection(programme: Programme): ProgrammePathwayProjection {
  const modules = programme.modules ?? [];
  const choiceSlotDefinitions = programme.electiveDefinitions ?? [];
  const assignedElectiveModuleIds = new Set(
    choiceSlotDefinitions.flatMap((definition) =>
      (definition.groups ?? []).flatMap((group) => group.moduleIds ?? []),
    ),
  );
  const mandatoryModules = modules.filter(
    (module) => !module.isElective && !assignedElectiveModuleIds.has(module.id),
  );
  const selectedModuleIds = new Set(mandatoryModules.map((module) => module.id));
  const incompleteChoiceSlots: string[] = [];

  const choiceSlots = choiceSlotDefinitions.map((choiceSlot) => {
    const optionSelections = (choiceSlot.groups ?? []).map((option) => ({
      option,
      selection: getElectiveOptionSelection(option, modules),
    }));
    const representative =
      optionSelections.find(
        ({ selection }) => selection.selectionCountValid && selection.selectionComplete,
      ) ?? optionSelections[0];
    const representativeOption = representative
      ? {
          option: representative.option,
          selectedModules: representative.selection.selectedModules,
          credits: representative.selection.credits,
          complete:
            representative.selection.selectionCountValid &&
            representative.selection.selectionComplete &&
            Number(choiceSlot.credits ?? 0) > 0 &&
            representative.selection.credits === Number(choiceSlot.credits ?? 0),
        }
      : undefined;
    const complete = representativeOption?.complete ?? false;

    if (!complete) {
      incompleteChoiceSlots.push(choiceSlotLabel(choiceSlot));
    }
    representativeOption?.selectedModules.forEach((module) => selectedModuleIds.add(module.id));

    return {
      choiceSlot,
      representativeOption,
      optionCount: optionSelections.length,
      complete,
    };
  });

  const selectedModules = modules.filter((module) => selectedModuleIds.has(module.id));
  const projectedCredits = selectedModules.reduce(
    (sum, module) => sum + Number(module.credits ?? 0),
    0,
  );
  const declaredCredits = Number(programme.totalCredits ?? 0);
  const unassignedElectiveModules = modules.filter(
    (module) => module.isElective && !assignedElectiveModuleIds.has(module.id),
  );

  let status: AwardCreditStatus = "aligned";
  if (declaredCredits <= 0 || incompleteChoiceSlots.length > 0) {
    status = "incomplete";
  } else if (declaredCredits > projectedCredits) {
    status = "shortfall";
  } else if (declaredCredits < projectedCredits) {
    status = "excess";
  }

  return {
    mandatoryModules,
    choiceSlots,
    selectedModules,
    unassignedElectiveModules,
    declaredCredits,
    projectedCredits,
    status,
    incompleteChoiceSlots,
  };
}

/**
 * Checks whether every programme version can deliver the representative route.
 *
 * A route module is covered when it is assigned to any stage in the version.
 * Stage credit targets are compared with the parent award only once every
 * stage has one.
 */
export function getProgrammeVersionPathwayCoverage(
  programme: Programme,
  pathway: ProgrammePathwayProjection = getProgrammePathwayProjection(programme),
): ProgrammeVersionPathwayCoverage[] {
  const awards = programme.embeddedAwards ?? [];

  return (programme.versions ?? []).map((version) => {
    const stages = version.stages ?? [];
    const assignedModuleIds = new Set(stages.flatMap((stage) => stageModuleIds(stage)));
    const coveredModules = pathway.selectedModules.filter((module) =>
      assignedModuleIds.has(module.id),
    );
    const missingModules = pathway.selectedModules.filter(
      (module) => !assignedModuleIds.has(module.id),
    );
    const coveredCredits = coveredModules.reduce(
      (sum, module) => sum + Number(module.credits ?? 0),
      0,
    );
    const stageCreditsTarget = stages.reduce(
      (sum, stage) => sum + Number(stage.creditsTarget ?? 0),
      0,
    );

    let status: ProgrammeVersionCoverageStatus = "covered";
    if (stages.length === 0) {
      status = "no-structure";
    } else if (missingModules.length > 0) {
      status = "missing-modules";
    } else if (pathway.incompleteChoiceSlots.length > 0) {
      // Not the route's status, which is also incomplete for an unset parent
      // total: that asks for a total, and the route itself may be whole.
      status = "pathway-incomplete";
    } else if (
      // A stage target left at zero is unset, so the sum is no total yet.
      stages.every((stage) => Number(stage.creditsTarget ?? 0) > 0) &&
      Number(programme.totalCredits ?? 0) > 0 &&
      stageCreditsTarget !== Number(programme.totalCredits ?? 0)
    ) {
      status = "credit-target-mismatch";
    }

    return {
      version,
      stageCount: stages.length,
      coveredModules,
      missingModules,
      coveredCredits,
      stageCreditsTarget,
      applicableAwards: awards.filter((award) => awardAppliesToVersion(award, version)),
      status,
    };
  });
}
